import { ref, Ref, watch, defineComponent } from 'vue';
import {
  useNamespace,
  getEditorEmits,
  getCascaderProps,
} from '@ibiz-template/vue3-util';
import { ICascader } from '@ibiz/model-core';
import { CascaderEditorController } from '../cascader-editor.controller';
import { IBizCommonRightIcon } from '../../common/right-icon/right-icon';
import { usePopstateListener } from '../../../util';
import './ibiz-cascader.scss';

type CascaderOption = {
  /**
   * @description 是否加载过
   * @type {boolean}
   */
  isLoaded: boolean;
  /**
   * @description 选项对应的值
   * @type {string}
   */
  value: string;
  /**
   * @description 选项对应的文本
   * @type {string}
   */
  text: string;
  /**
   * @description 原始数据
   * @type {IData}
   */
  data: IData;
  /**
   * @description 选项文字颜色
   * @type {string}
   */
  color?: string;
  /**
   * @description 是否禁用选项
   * @type {boolean}
   */
  disabled?: boolean;
  /**
   * @description 子选项列表
   * @type {CascaderOption[]}
   */
  children?: CascaderOption[];
  /**
   * @description 为对应列添加额外的 class
   * @type {string}
   */
  className?: string;
};

/**
 * 移动端级联选择器
 * @primary
 * @description 使用van-cascader组件，用于选择具有级联关系的数据。支持编辑器类型包含：`移动端级联选择器`
 * @editorparams {name:separator,parameterType:string,defaultvalue:'/',description:分隔符，用于拼接选择的值}
 * @editorparams {name:readonly,parameterType:boolean,defaultvalue:false,description:设置编辑器是否为只读态}
 * @editorparams {name:leaffield,parameterType:string,defaultvalue:undefined,description:设置数据的叶子节点属性（boolean类型或是否逻辑类型），如果数据为叶子节点则点击数据时不展开子数据，直接选中该数据}
 * @ignoreprops  autoFocus | overflowMode
 * @ignoreemits  blur | focus | infoTextChange | enter
 */
export const IBizCascader = defineComponent({
  name: 'IBizCascader',
  props: getCascaderProps<CascaderEditorController>(),
  emits: getEditorEmits(),
  setup(props, { emit }) {
    const ns = useNamespace('cascader');

    const c = props.controller!;

    const editorModel: ICascader = c.model;

    const valueItems = editorModel.editorItems || [];

    const selectValue: Ref<string | undefined> = ref(undefined);

    // 连接符
    let separator = '/';
    // 叶子节点属性
    let leafField: string | undefined;

    if (editorModel.editorParams) {
      if (editorModel.editorParams.separator)
        separator = editorModel.editorParams.separator;
      if (editorModel.editorParams.leaffield)
        leafField = editorModel.editorParams.leaffield;
    }

    // 树节点数据
    const nodes: Ref<CascaderOption[]> = ref([]);
    // 平铺数据集
    const items: Ref<CascaderOption[]> = ref([]);
    // 值项数据
    const valueItemData: Ref<{ name: string; value?: string }[]> = ref(
      valueItems.map(item => ({ name: item.id!, value: undefined })),
    );
    const show = ref(false);
    const isLoading = ref(false);

    const onClose = () => {
      show.value = false;
    };

    watch(
      () => props.data,
      newVal => {
        if (newVal)
          valueItemData.value.forEach(valueItem => {
            valueItem.value = newVal[valueItem.name];
          });
      },
      {
        immediate: true,
        deep: true,
      },
    );

    // 处理查询参数
    const handleQueryParams = async (index: number, value?: string) => {
      const context = c.context.clone();
      const params = { ...c.params };
      // 如果上级和下级具有父子关系，则根据关系字段添加查询参数
      if (index > 0) {
        const { appDataEntityId: parentAppDataEntityId } =
          valueItems[index - 1];
        const { appId, appDataEntityId: childAppDataEntityId } =
          valueItems[index];
        const appDataEntity = await ibiz.hub.getAppDataEntity(
          childAppDataEntityId!,
          appId,
        )!;
        const { minorAppDERSs } = appDataEntity;
        if (minorAppDERSs) {
          const appDeRSs = minorAppDERSs.find(
            DERSs => DERSs.majorAppDataEntityId === parentAppDataEntityId,
          );
          if (appDeRSs && appDeRSs.parentAppDEFieldId && value)
            Object.assign(params, {
              [`n_${appDeRSs.parentAppDEFieldId!.toLowerCase()}_eq`]: value,
            });
        }
      }
      return { context, params };
    };

    /**
     * @description 完成选择
     * @param {IData} $evt
     */
    const onFinishSelect = ($evt: IData): void => {
      onClose();
      const { selectedOptions: selection = [] } = $evt;
      const selectedText = selection.map((select: IData) => select.text);
      valueItemData.value.forEach((valueItem: IData, index) => {
        // 设置新值
        valueItem.value =
          index < selection.length ? selection[index].value : undefined;
        emit('change', valueItem.value, valueItem.name);
      });
      emit(
        'change',
        selectedText.length > 0 ? selectedText.join(separator) : null,
      );
    };

    /**
     * @description 懒加载
     * @param {number} [index=0] 层级
     * @param {CascaderOption} [parent] 父节点
     * @returns {*}  {Promise<void>}
     */
    const lazyLoad = async (
      parent?: CascaderOption,
      index: number = 0,
      selections: CascaderOption[] = [],
    ): Promise<void> => {
      // 无数据和异常时设置上一层级选中数据
      const onLastSelected = (): void => {
        const selectedVal = index > 1 ? selections[index - 2].value : undefined;
        selectValue.value = undefined;
        setTimeout(() => {
          selectValue.value = selectedVal;
        });
      };
      try {
        isLoading.value = true;
        const { appDataEntityId, appDEDataSetId } = valueItems[index];
        if (appDataEntityId && appDEDataSetId) {
          const { context, params } = await handleQueryParams(
            index,
            parent?.value,
          );
          const app = ibiz.hub.getApp(editorModel.appId);
          const response = await app.deService.exec(
            appDataEntityId,
            appDEDataSetId,
            context,
            params,
          );
          if (response.ok && Array.isArray(response.data)) {
            const children: CascaderOption[] = response.data.map(data => ({
              data,
              isLoaded: false,
              value: data.srfkey,
              text: data.srfmajortext
                ? data.srfmajortext
                : ibiz.i18n.t('editor.cascader.title', {
                    index,
                  }),
              children:
                index === valueItems.length - 1 ||
                (leafField && data[leafField])
                  ? undefined
                  : [],
            }));
            if (parent) {
              parent.isLoaded = true;
              parent.children = children.length ? children : undefined;
            } else {
              nodes.value = children.length
                ? children
                : [
                    {
                      data: {},
                      value: 'empty',
                      isLoaded: true,
                      disabled: true,
                      text: ibiz.i18n.t('editor.cascader.noData'),
                      className: ns.e('empty'),
                    },
                  ];
            }
            items.value.push(...children);
            if (!children.length) {
              onLastSelected();
            } else {
              // 设置回显选中
              // const selectedVal = valueItemData.value[index].value;
              // const selectedChild = children.find(
              //   child => child.value === selectedVal,
              // );
              // if (selectedChild) {
              //   selectValue.value = selectedVal;
              //   onChange({
              //     tabIndex: index,
              //     value: selectedChild.value,
              //     selectedOptions: [...selections, selectedChild],
              //   });
              // }
            }
          }
        } else {
          onLastSelected();
        }
      } catch (error) {
        ibiz.log.error(error);
        onLastSelected();
      } finally {
        isLoading.value = false;
      }
    };

    /**
     * 值变更
     * @param $event
     * @returns
     */
    const onChange = ($evt: {
      tabIndex: number;
      value: string;
      selectedOptions: CascaderOption[];
    }) => {
      const { tabIndex, value, selectedOptions } = $evt;
      const index = tabIndex + 1;
      // 下标和值项数量一致 表示已经选择完成
      const selected = items.value.find(item => item.value === value);
      // 加载过的数据，最后一层或者是叶子节点的数据不加载数据
      if (
        !selected ||
        selected.isLoaded ||
        index === valueItems.length ||
        (leafField && selected.data[leafField])
      )
        return;
      lazyLoad(selected, index, selectedOptions);
    };

    /**
     * 重载数据
     */
    const onRestLoad = async (): Promise<void> => {
      // 每次打开需要重新加载
      nodes.value = [];
      items.value = [];
      await lazyLoad();
    };

    const onBlur = () => {
      emit('blur');
    };

    const onFocus = () => {
      emit('focus');
    };

    const openPopup = () => {
      if (props.disabled || props.readonly) return;
      onRestLoad();
      show.value = !show.value;
    };

    // 监听popstate事件
    usePopstateListener(onClose);

    return {
      c,
      ns,
      show,
      nodes,
      isLoading,
      selectValue,
      valueItemData,
      onBlur,
      onFocus,
      onClose,
      onChange,
      openPopup,
      onFinishSelect,
    };
  },
  render() {
    return (
      <div
        class={[
          this.ns.b(),
          this.disabled ? this.ns.m('disabled') : '',
          this.readonly ? this.ns.m('readonly') : '',
        ]}
        v-loading={this.isLoading}
      >
        {this.readonly && this.value}
        {!this.readonly && (
          <van-field
            modelValue={this.value}
            readonly
            disabled={this.disabled}
            placeholder={this.c.placeHolder}
            onBlur={this.onBlur}
            onFocus={this.onFocus}
            onClick={this.openPopup}
          >
            {{
              'right-icon': !this.readonly && (
                <IBizCommonRightIcon></IBizCommonRightIcon>
              ),
            }}
          </van-field>
        )}
        <van-popup
          round
          teleport='body'
          position='bottom'
          v-model:show={this.show}
          close-on-popstate={true}
        >
          <van-cascader
            options={this.nodes}
            v-model={this.selectValue}
            class={this.ns.e('content')}
            title={this.c.placeHolder}
            onClose={this.onClose}
            onChange={this.onChange}
            onFinish={this.onFinishSelect}
          />
        </van-popup>
      </div>
    );
  },
});
